import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { pickInitialSeasonId, saveSeasonPref } from "@/lib/seasonPref";
import { computeRanks } from "@/lib/leaderboardRanks";
import { Settings as SettingsIcon } from "lucide-react";
import { formatDMY } from "@/lib/date";

export default function Stats() {
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [photogs, setPhotogs] = useState([]);
  const [selectedPid, setSelectedPid] = useState("");
  const [profile, setProfile] = useState(null);
  // 4 toggle states: "inherit" (default, follows club's POY rule), or
  // an explicit "all" / "top1" / "top2" override. The user's choice is
  // remembered between visits.
  const [scoring, setScoring] = useState(
    () => localStorage.getItem("ccs_leaderboard_scoring") || "inherit"
  );
  const [clubScoring, setClubScoring] = useState("top2");

  // The effective mode actually sent to the API.
  const effectiveScoring = scoring === "inherit" ? clubScoring : scoring;

  useEffect(() => {
    localStorage.setItem("ccs_leaderboard_scoring", scoring);
  }, [scoring]);

  useEffect(() => {
    (async () => {
      const [s, p, club] = await Promise.all([
        api.get("/seasons"),
        api.get("/photographers"),
        api.get("/clubs/me").catch(() => ({ data: {} })),
      ]);
      setSeasons(s.data);
      setPhotogs(p.data);
      setClubScoring(club.data?.poy_scoring_mode || "top2");
      setSeasonId(pickInitialSeasonId(s.data));
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const params = { scoring: effectiveScoring };
      if (seasonId) params.season_id = seasonId;
      const { data } = await api.get("/stats/leaderboard", { params });
      setLeaderboard(data);
    })();
  }, [seasonId, effectiveScoring]);

  useEffect(() => {
    if (!selectedPid) { setProfile(null); return; }
    (async () => {
      const { data } = await api.get(`/stats/photographer/${selectedPid}`, {
        params: { scoring: effectiveScoring },
      });
      setProfile(data);
    })();
  }, [selectedPid, effectiveScoring]);

  return (
    <div className="p-4 md:p-8 w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="ccs-label mb-2">Insights</div>
          <h1 className="text-4xl font-semibold tracking-tight">Statistics</h1>
        </div>
        <select
          className="ccs-input" style={{ width: 220 }}
          value={seasonId} onChange={(e) => { setSeasonId(e.target.value); saveSeasonPref(e.target.value); }}
          data-testid="stats-season-filter"
        >
          <option value="">All seasons</option>
          {seasons.map((s) => <option key={s.id} value={s.id}>{s.season_name}</option>)}
        </select>
      </div>

      {/* Flex layout: leaderboard panel takes 60% of the row, photographer
          profile fills the remaining 40% with a small gap between them. */}
      <div className="flex flex-col md:flex-row gap-4 items-start">
        <div className="ccs-card md:basis-3/5 md:flex-shrink-0">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <h2 className="text-lg font-medium">Club Leaderboard</h2>
            <ScoringToggle scoring={scoring} onChange={setScoring} clubScoring={clubScoring} showConfigureLink />
          </div>
          {leaderboard.length === 0 ? (
            <div className="muted-text text-sm">No scored entries.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-2 justify-items-start">
              {splitColumns(leaderboard).map((col, ci) => (
                <table
                  key={ci}
                  className="ccs-table"
                  style={{ width: "auto", tableLayout: "fixed" }}
                  data-testid={`leaderboard-column-${ci}`}
                >
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>Rank</th>
                      <th style={{ width: 130 }}>Photographer</th>
                      <th className="text-right" style={{ width: 50 }}>Total</th>
                      <th className="text-right" style={{ width: 44 }}>Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {col.map((r) => (
                      <tr
                        key={r.photographer_id}
                        onClick={() => setSelectedPid(r.photographer_id)}
                        className={
                          "cursor-pointer " +
                          (selectedPid === r.photographer_id ? "bg-amber-500/5" : "")
                        }
                        data-testid={`leaderboard-row-${r.photographer_code}`}
                      >
                        <td className="mono text-amber-400 font-medium">
                          <span>#{r._rank}</span>
                          {r._tied && (
                            <span
                              className="ml-1.5 inline-block align-middle px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30"
                              title="Tied on total score — countback rules apply for the final award"
                              data-testid="leaderboard-tied-pill"
                            >
                              Tied
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="font-medium text-sm">{r.photographer_name}</div>
                          <div className="text-[10px] mono muted-text">{r.photographer_code}</div>
                        </td>
                        <td className="mono text-right font-semibold">{r.total_score}</td>
                        <td className="mono text-right muted-text text-sm">{r.avg_score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}
            </div>
          )}
        </div>

        <div className="ccs-card md:basis-2/5 flex-1 md:min-w-0">
          <div className="flex flex-col gap-3 mb-4">
            <div>
              <h2 className="text-lg font-medium">Photographer Profile</h2>
              <div className="text-xs muted-text mt-1">
                Click any photographer on the left, or pick one here.
              </div>
            </div>
            <select
              className="ccs-input w-full"
              value={selectedPid}
              onChange={(e) => setSelectedPid(e.target.value)}
              data-testid="profile-photographer-select"
            >
              <option value="">Choose photographer…</option>
              {photogs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.photographer_code} — {p.display_name}
                </option>
              ))}
            </select>
          </div>

          {profile ? (
            <PhotographerProfile profile={profile} />
          ) : (
            <div className="muted-text text-sm py-6 text-center">
              Select a photographer to see their per-competition breakdown.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PhotographerProfile({ profile }) {
  const { photographer, stats, competitions } = profile;
  const countedTotal = stats.total_score || 0;
  const excludedTotal = stats.excluded_total || 0;

  return (
    <>
      {/* Headline numbers — big & clear. 2×2 grid fits the narrower right column. */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <BigStat
          label="Season total"
          value={countedTotal}
          tone="positive"
          testId="profile-total"
          subtitle="Counts toward the season"
        />
        <BigStat
          label="Average"
          value={stats.avg_score || 0}
          testId="profile-avg"
          subtitle={`${stats.entries} counted ${stats.entries === 1 ? "entry" : "entries"}`}
        />
        <BigStat
          label="Best score"
          value={stats.max_score || 0}
          testId="profile-best"
        />
        <BigStat
          label="Not counted"
          value={excludedTotal}
          tone="muted"
          testId="profile-excluded-total"
          subtitle="From friendlies / excluded rounds"
        />
      </div>

      <div className="text-xs mono muted-text mb-4">
        {stats.firsts}× 1st · {stats.seconds}× 2nd · {stats.thirds}× 3rd ·{" "}
        {stats.hc}× HC · {stats.c}× C
      </div>

      <div className="ccs-label mb-2">Competitions entered</div>
      {competitions && competitions.length > 0 ? (
        <CompetitionsBreakdown
          competitions={competitions}
          countedTotal={countedTotal}
          excludedTotal={excludedTotal}
        />
      ) : (
        <div className="muted-text text-sm py-4">
          {photographer.display_name} hasn&rsquo;t entered any competitions yet.
        </div>
      )}
    </>
  );
}

function CompetitionsBreakdown({ competitions, countedTotal, excludedTotal }) {
  const counted = competitions.filter((c) => c.counted);
  const notCounted = competitions.filter((c) => !c.counted);

  return (
    <div className="space-y-6">
      <CompTable
        title="Counted toward season total"
        rows={counted}
        total={countedTotal}
        emptyMessage="No competitions are contributing to the season total yet."
        totalTone="text-amber-400"
        testId="profile-counted-table"
      />
      {notCounted.length > 0 && (
        <CompTable
          title="Not counted (excluded comps, friendlies, etc.)"
          rows={notCounted}
          total={excludedTotal}
          emptyMessage=""
          totalTone="muted-text"
          testId="profile-not-counted-table"
          subdued
        />
      )}
    </div>
  );
}

function CompTable({ title, rows, total, emptyMessage, totalTone, testId, subdued }) {
  if (rows.length === 0) {
    return (
      <div>
        <div className="ccs-label mb-2 flex items-center gap-2">
          {title}
        </div>
        <div className="muted-text text-sm py-3">{emptyMessage}</div>
      </div>
    );
  }
  return (
    <div data-testid={testId}>
      <div className="ccs-label mb-2 flex items-center gap-2">
        {title}
        <span className="muted-text">·</span>
        <span className="muted-text">{rows.length} comp{rows.length === 1 ? "" : "s"}</span>
      </div>
      <table className={"ccs-table " + (subdued ? "opacity-80" : "")}>
        <thead>
          <tr>
            <th style={{ width: 70 }}>Date</th>
            <th>Competition</th>
            <th style={{ width: 90 }} className="text-right">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.competition_id} data-testid={`profile-comp-row-${c.competition_id}`}>
              <td className="mono muted-text text-xs">{formatDMY(c.competition_date)}</td>
              <td>
                <div className="font-medium text-sm">{c.competition_name}</div>
                <div className="text-[10px] muted-text mono">
                  {(c.competition_type || "")}{c.competition_type && c.category ? " · " : ""}{c.category || ""}
                </div>
                <div className="text-xs muted-text mt-1 space-y-0.5">
                  {c.image_titles.map((it, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="truncate flex-1">{it.image_title || "(untitled)"}</span>
                      <span className="mono">{it.score}</span>
                      {it.placement && it.placement !== "NONE" && (
                        <span className="mono">{placementShort(it.placement)}</span>
                      )}
                    </div>
                  ))}
                </div>
              </td>
              <td className="mono text-right text-base font-semibold">{c.subtotal}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr
            style={{ borderTop: "2px solid #2a2a2a" }}
            data-testid={`${testId}-total`}
          >
            <td colSpan={2} className="text-right text-xs uppercase tracking-wider muted-text">
              {subdued ? "Excluded total" : "Season total so far"}
            </td>
            <td className={"mono text-right text-lg font-semibold " + totalTone}>{total}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function BigStat({ label, value, subtitle, tone, testId }) {
  const colour =
    tone === "positive" ? "text-amber-400" : tone === "muted" ? "muted-text" : "";
  return (
    <div
      className="py-3 px-4"
      style={{ background: "#131313", border: "1px solid #1f1f1f" }}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wider muted-text">{label}</div>
      <div className={`mono text-3xl font-semibold ${colour}`}>{value}</div>
      {subtitle && <div className="text-xs muted-text mt-1">{subtitle}</div>}
    </div>
  );
}

function splitColumns(rows) {
  // Stamp rank first (Olympic ties: #1, #2, #2, #4) so it remains continuous
  // across the two columns (#1-#N down the left, #N+1-end down the right).
  const ranked = computeRanks(rows);
  const half = Math.ceil(ranked.length / 2);
  return [ranked.slice(0, half), ranked.slice(half)];
}

function placementShort(p) {
  switch (p) {
    case "FIRST": return "1st";
    case "SECOND": return "2nd";
    case "THIRD": return "3rd";
    case "HIGHLY_COMMENDED": return "HC";
    case "COMMENDED": return "C";
    default: return "";
  }
}



/* Tiny segmented control for the leaderboard scoring rule. The user can
   flip between "Inherit" (follow club's Settings → Scoring rules choice),
   "Best image", "Top 2", and "Every entry" without changing the club-wide
   POY setting. The preference is persisted in localStorage, defaulting
   to "inherit" on first visit so the leaderboard matches the trophy. */
const SCORING_OPTIONS = [
  { value: "inherit", short: "Inherit", long: "Follow club's Scoring rules in Settings" },
  { value: "top1",    short: "Best",    long: "Best image per round" },
  { value: "top2",    short: "Top 2",   long: "Top 2 images per round" },
  { value: "all",     short: "All",     long: "Every entry" },
];

const RESOLVED_LABEL = {
  all: "every entry",
  top1: "best image",
  top2: "top 2",
};

function ScoringToggle({ scoring, onChange, clubScoring, showConfigureLink = false }) {
  const { user } = useAuth();
  return (
    <div className="inline-flex items-center gap-2 flex-wrap" data-testid="leaderboard-scoring-toggle">
      <span className="muted-text text-xs">Total based on:</span>
      <div className="inline-flex" style={{ background: "#0f0f0f", border: "1px solid #1f1f1f" }}>
        {SCORING_OPTIONS.map((o, i) => {
          const active = o.value === scoring;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              className="px-3 py-1 text-xs font-medium transition-colors"
              style={{
                background: active ? "#1d160a" : "transparent",
                color: active ? "#fbbf24" : "#999",
                borderLeft: i > 0 ? "1px solid #1f1f1f" : "none",
              }}
              title={o.long}
              data-testid={`leaderboard-scoring-${o.value}`}
            >
              {o.short}
            </button>
          );
        })}
      </div>
      {scoring === "inherit" && (
        <span className="muted-text text-xs" data-testid="leaderboard-scoring-resolved">
          → {RESOLVED_LABEL[clubScoring] || clubScoring}
        </span>
      )}
      {showConfigureLink && user?.role === "ADMIN" && (
        <Link
          to="/settings"
          className="muted-text text-xs hover:text-amber-400 inline-flex items-center gap-1"
          title="Change the club-wide scoring rule"
          data-testid="leaderboard-scoring-configure-link"
        >
          <SettingsIcon size={11} /> Configure
        </Link>
      )}
    </div>
  );
}
